import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // SPRINT-5: i18n
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plus, X, DollarSign, Calendar, Hotel, Plane, FileText, CreditCard, ChevronRight } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { useContent } from '../hooks/useContent';
import api from '../utils/api';
import { currencySymbol, currencyPolicyReasonKey } from '../utils/currencyPolicy';
// TREATX-TAX-5 — provider treatment VAT (provider owns the determination).
import ProviderTaxSection from './offer/ProviderTaxSection';
import { ResidenceForm } from './patient/ResidencePrompt';
// PROVIDER-OWNED-DRAFT-01 — currencies a DRAFT may PROPOSE while the legal
// matrix is still unresolved. Mirrors the product-supported set the backend
// policy authority defines; it grants nothing — the backend refuses to ISSUE
// an offer whose currency the matrix has not allowed.
const DRAFT_PROPOSAL_CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP'];
import {
  providerTaxDefaults,
  validateProviderTax,
  buildProviderTaxPayload,
  TAX_BASIS_EXCLUSIVE,
  TAX_TREATMENT_EXEMPT,
} from '../utils/providerTax';

// Bridge: convert Shadcn-style toast calls to sonner
const toast = (opts) => {
  if (typeof opts === 'string') return sonnerToast(opts);
  const { title, description, variant } = opts || {};
  if (variant === 'destructive') return sonnerToast.error(title || 'Error', { description });
  return sonnerToast.success(title || 'Success', { description });
};

// FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 (2026-06-12):
// Additive optional capabilities lifted from the lean Quotes→Respond
// modal. Defaults are off → existing Sales Rep behaviour preserved.
//
//   enableTemplates     → render offer-template picker (GET /clinic/offer-templates)
//   enableCampaigns     → render active-campaign picker (GET /clinic/campaigns)
//   showPatientContext  → read-only positioning panel (tags / VIPs / campaign)
//                         sourced from canonical /api/clinic/profile
//   leadId              → optional context id (not used for endpoints — kept
//                         for future per-lead context).
//
// Backend payload already accepts template_id, campaign_id,
// discount_percentage on offer_service.create_offer — no new
// persistence, no new endpoint.
// SSO-CLOSURE-C — the patient's CURRENT offer selection (null = standard).
// The frozen attribution is the ORIGINAL request; when the patient later
// changed their service option the newest selection event is what they now
// want, so the composer must not silently reinstate the old offer. A
// jurisdiction-withheld event can never be reinstated as a selection.
function latestPatientCampaignId(lead) {
  const events = Array.isArray(lead?.selection_events) ? lead.selection_events : [];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i];
    if (!ev || ev.campaign_withheld_by_surface === true) continue;
    return ev.campaign_id || null;
  }
  return lead?.campaign_attribution?.campaign_id || null;
}

const OfferComposer = ({
  lead,
  existingOffer,
  onOfferSent,
  onCancel,
  enableTemplates = false,
  enableCampaigns = false,
  showPatientContext = false,
  leadId = null,
  highestVersion = 0,
}) => {
  const { t } = useTranslation(); // SPRINT-5: i18n hook
  const [loading, setLoading] = useState(false);

  // TREATX-BATCH-10B — Cancellation & Refund Policy is now a
  // separate concept from provider-authored Terms & Conditions.
  // The current canonical policy body is displayed read-only below the
  // Terms textarea and will be snapshotted immutably by the backend
  // (`offer_service._freeze_cancellation_policy_snapshot`) at send time.
  const { content: cancellationPolicyContent } = useContent(
    'cancellation_and_refund_conditions',
    { flow: 'offer_send' },
  );
  
  // P3-REVISION: Determine if this is a revision
  const isRevision = !!existingOffer;
  // MULTIPLE-ACTIONABLE-ALTERNATIVES — intent toggle when composing from an existing offer.
  const [composerMode, setComposerMode] = useState('revise');
  // TREATX-BATCH-1.1 — accept the backend snake_case shape returned by
  // /api/clinic/leads/{id}/offers (base_price / additional_services /
  // deposit_amount / version_number / …). The prior camelCase-only
  // reads left every revision-mode prefill blank so the composer
  // silently reverted to defaults. Read both shapes with snake_case
  // taking precedence when present.
  // OWNER-OFFER-REVISION-02 — the next version number belongs to the LEAD's
  // lineage, not to the row being revised: revising an older version still
  // produces max(version)+1, so the banner must not promise "v2" when v2 exists.
  const nextVersion = existingOffer
    ? (Math.max(highestVersion || 0,
                (existingOffer.version_number ?? existingOffer.version ?? 1)) + 1)
    : 1;

  // P3-REVISION: Initialize with existing offer data if revising
  const [offerData, setOfferData] = useState({
    treatment: existingOffer?.treatment_name || existingOffer?.treatment || lead?.treatment_interest || lead?.treatment || '',
    basePrice: existingOffer?.base_price ?? existingOffer?.basePrice ?? '',
    // PHASE 2B/B1 — no client default; the backend currency policy decides the allowed set.
    currency: existingOffer?.currency || '',
    includedItems: existingOffer?.included_items || existingOffer?.includedItems || [
      { name: 'Medical Procedure', included: true },
      { name: 'Pre-operative Tests', included: true },
      { name: 'Post-operative Check-ups (3)', included: true },
      { name: 'Medications', included: true }
    ],
    additionalServices: existingOffer?.additional_services || existingOffer?.additionalServices || [],
    hotelNights: existingOffer?.hotel_nights ?? existingOffer?.hotelNights ?? '',
    hotelType: existingOffer?.hotel_type || existingOffer?.hotelType || '',
    airportTransfer: (existingOffer?.airport_transfer ?? existingOffer?.airportTransfer) ?? true,
    proposedDates: existingOffer?.proposed_dates || existingOffer?.proposedDates || '',
    validUntil: (() => {
      // TREATX-BATCH-1.1 — quote rows persist `valid_until` (ISO
      // date), not `valid_days`. Derive integer days from the
      // predecessor's stored window so revision-mode prefill is
      // non-empty and the client-side submit gate no longer blocks
      // 'Send Revised Offer' with 'Invalid validity period'.
      // A new offer starts at the backend's own default window (30 days).
      if (!existingOffer) return '30';
      if (existingOffer.valid_days != null) return String(existingOffer.valid_days);
      if (existingOffer.validUntil && !/^\d+$/.test(String(existingOffer.validUntil))) {
        // ISO date string — derive days relative to created_at.
        const end = new Date(existingOffer.validUntil);
        const start = existingOffer.created_at ? new Date(existingOffer.created_at) : new Date();
        const ms = end.getTime() - start.getTime();
        if (Number.isFinite(ms) && ms > 0) return String(Math.max(1, Math.ceil(ms / 86400000)));
      }
      if (existingOffer.valid_until) {
        const end = new Date(existingOffer.valid_until);
        const start = existingOffer.created_at ? new Date(existingOffer.created_at) : new Date();
        const ms = end.getTime() - start.getTime();
        if (Number.isFinite(ms) && ms > 0) return String(Math.max(1, Math.ceil(ms / 86400000)));
      }
      return existingOffer.validUntil || '';
    })(),
    notes: existingOffer?.notes_patient_visible || existingOffer?.notes || '',
    // P3-FIX-2: Payment structure customization
    depositType: existingOffer?.deposit_type || existingOffer?.depositType || 'percentage',
    depositValue: existingOffer?.deposit_value ?? existingOffer?.depositValue ?? 30,
    paymentStructure: existingOffer?.payment_structure || existingOffer?.paymentStructure || 'single',
    installmentCount: existingOffer?.installment_count ?? existingOffer?.installmentCount ?? 2,
    terms: existingOffer?.terms || '',
    // FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 — additive fields
    // already accepted by offer_service.create_offer.
    templateId: existingOffer?.template_id || null,
    // SSO-D1 — a NEW offer starts from what the patient actually selected
    // (the frozen C4A attribution on the lead). The rep stays free to change
    // or clear it; the patient's original selection is displayed read-only
    // beside the form and is never overwritten by the rep's choice.
    campaignId: existingOffer?.campaign_id
      || (existingOffer ? null : latestPatientCampaignId(lead)),
    discountPercentage: existingOffer?.discount_percentage ?? '',
    // TREATX-TAX-5 — provider-declared treatment VAT (provider's own liability).
    ...providerTaxDefaults(existingOffer),
  });

  // FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 — lifted state
  const [templateList, setTemplateList] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [positioning, setPositioning] = useState({
    tags: [], vips: [],
    campaign: { has_active_campaign: false, campaign_description: '' },
  });

  // PHASE 2B/B1 — backend-authoritative allowed contract currencies.
  // null = loading; {resolved:false} = fail-closed (issuance blocked).
  const [currencyPolicy, setCurrencyPolicy] = useState(null);
  const policyLeadId = lead?.id || leadId;
  useEffect(() => {
    if (!policyLeadId) { setCurrencyPolicy({ resolved: false, reason: 'lead_missing' }); return; }
    let cancelled = false;
    api.get(`/clinic-leads/${policyLeadId}/currency-policy`)
      .then(res => {
        if (cancelled) return;
        const pol = res.data || {};
        setCurrencyPolicy(pol);
        const allowed = pol.allowed_currencies || [];
        setOfferData(d => (pol.resolved && !allowed.includes(d.currency))
          ? { ...d, currency: allowed[0] || '' }
          : d);
      })
      .catch(() => { if (!cancelled) setCurrencyPolicy({ resolved: false, reason: 'policy_fetch_failed' }); });
    return () => { cancelled = true; };
  }, [policyLeadId]);
  const allowedCurrencies = currencyPolicy?.resolved ? (currencyPolicy.allowed_currencies || []) : [];
  const policyBlocked = !!currencyPolicy && !currencyPolicy.resolved;
  const sym = currencySymbol(offerData.currency);

  useEffect(() => {
    if (!enableTemplates) return;
    api.get('/clinic/offer-templates')
       .then(res => setTemplateList(res.data?.templates || res.data || []))
       .catch(() => setTemplateList([]));
  }, [enableTemplates]);

  useEffect(() => {
    if (!enableCampaigns) return;
    api.get('/clinic/campaigns')
       .then(res => setCampaignList((res.data?.campaigns || res.data || []).filter(c => c.is_enabled || c.is_active)))
       .catch(() => setCampaignList([]));
  }, [enableCampaigns]);

  useEffect(() => {
    if (!showPatientContext) return;
    // FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 — read-only positioning panel
    // sourced from the canonical /api/clinic/profile endpoint. No new endpoint.
    api.get('/clinic/profile')
       .then(res => {
         const d = res.data || {};
         const ptBlock = d.positioning_tags || {};
         const vipBlock = d.vip_services || {};
         const tagCatalog = Object.fromEntries((ptBlock.catalog || []).map(x => [x.slug, x.display || x.slug]));
         const vipCatalog = Object.fromEntries((vipBlock.catalog || []).map(x => [x.slug, x.name || x.slug]));
         setPositioning({
           tags: (ptBlock.assigned || []).slice(0, 2).map(slug => ({
             label: tagCatalog[slug] || slug, tag: slug,
           })),
           vips: (vipBlock.assigned || []).slice(0, 2).map(slug => ({
             label: vipCatalog[slug] || slug, vip: slug,
           })),
           campaign: {
             has_active_campaign: d.commercial?.has_active_campaign === true,
             campaign_description: (typeof d.commercial?.campaign_description === 'string' && d.commercial.campaign_description.trim())
               ? d.commercial.campaign_description.trim() : '',
           },
         });
       })
       .catch(() => { /* additive: silent fail keeps the form usable */ });
  }, [showPatientContext, leadId]);

  const applyTemplate = (tpl) => {
    if (!tpl) {
      setOfferData(d => ({ ...d, templateId: null }));
      return;
    }
    setOfferData(d => ({
      ...d,
      templateId: tpl.id,
      campaignId: null,
      treatment: tpl.treatment_name || tpl.treatment_slug || d.treatment,
      basePrice: tpl.base_price != null ? String(tpl.base_price) : d.basePrice,
      currency: tpl.default_currency || tpl.currency || d.currency,
    }));
  };

  const applyCampaign = (camp) => {
    if (!camp) {
      setOfferData(d => ({ ...d, campaignId: null }));
      return;
    }
    setOfferData(d => ({
      ...d,
      campaignId: camp.campaign_id || camp.id,
      templateId: null,
      basePrice: camp.campaign_price_hint != null ? String(camp.campaign_price_hint) : d.basePrice,
      currency: camp.currency || d.currency,
      discountPercentage: camp.discount_percentage != null
        ? String(camp.discount_percentage)
        : d.discountPercentage,
    }));
  };

  const showPositioningBlock = showPatientContext && (
    ((positioning.tags?.length || 0) + (positioning.vips?.length || 0)) > 0
    || positioning.campaign?.has_active_campaign
  );

  // PHASE 2B/B1 — quick-add presets carry NO currency-denominated price:
  // the provider enters the unit price in the policy-resolved quote currency.
  const packageOptions = [
    { name: 'Hotel Accommodation (per night)', price: 0 },
    { name: 'Private Airport Transfer (round trip)', price: 0 },
    { name: 'VIP Concierge Service', price: 0 },
    { name: 'Local SIM Card', price: 0 },
    { name: 'City Tour', price: 0 },
    { name: 'Translator Service (per day)', price: 0 }
  ];

  const addAdditionalService = (service) => {
    if (!offerData.additionalServices.find(s => s.name === service.name)) {
      setOfferData(prev => ({
        ...prev,
        additionalServices: [...prev.additionalServices, { ...service, quantity: 1 }]
      }));
    }
  };

  const removeAdditionalService = (serviceName) => {
    setOfferData(prev => ({
      ...prev,
      additionalServices: prev.additionalServices.filter(s => s.name !== serviceName)
    }));
  };

  const updateServiceQuantity = (serviceName, quantity) => {
    setOfferData(prev => ({
      ...prev,
      additionalServices: prev.additionalServices.map(s =>
        s.name === serviceName ? { ...s, quantity: Math.max(1, quantity) } : s
      )
    }));
  };

  // SPRINT-2: Update unit price for additional service
  const updateServicePrice = (serviceName, price) => {
    setOfferData(prev => ({
      ...prev,
      additionalServices: prev.additionalServices.map(s =>
        s.name === serviceName ? { ...s, price: Math.max(0, parseFloat(price) || 0) } : s
      )
    }));
  };

  // TREATX-BATCH-10D — Composer PROVISIONAL PREVIEW ONLY.
  //
  // The three functions below (calculateTotalPrice, calculateDeposit,
  // calculatePaymentBreakdown) are non-authoritative provider-side
  // preview helpers. They exist SOLELY so the sales rep can see an
  // indicative total / deposit / installment breakdown while composing
  // an unsaved offer.
  //
  // They are NOT the financial authority:
  //   - Their outputs are NOT read by any backend code path. Grep
  //     `backend/` for `totalPrice`, `total_price`, `paymentBreakdown`,
  //     `payment_breakdown` — zero matches. The backend persists only
  //     raw commercial inputs (`base_price`, `additional_services[]`,
  //     `deposit_type`, `deposit_value`, `deposit_amount`,
  //     `payment_structure`, `installment_count`).
  //   - Patient web reads the backend-derived `offer.financial_summary`
  //     (see `MyOffers.jsx` after Batch-10D). It NEVER calls these
  //     preview helpers or any client-side calculator on patient
  //     responses.
  //   - PDF renders via `backend/services/quote_financials.compute_quote_totals`.
  //   - Acceptance uses the same immutable quote document.
  //
  // A future developer must not repurpose these functions on any
  // patient-facing surface. If more advanced provider preview logic is
  // needed, extend these under the same non-authoritative contract.
  const calculateTotalPrice = () => {
    const base = parseFloat(offerData.basePrice) || 0;
    const additional = offerData.additionalServices.reduce((sum, service) =>
      sum + (service.price * service.quantity), 0
    );
    return base + additional;
  };

  // TREATX-TAX-5 — composer-only VAT preview, under the SAME
  // non-authoritative contract as the helpers above. The provider needs to
  // see the patient total before sending; the persisted truth is still the
  // backend `financial_summary` derived by `quote_financials`.
  const taxPreview = () => {
    const entered = calculateTotalPrice();
    if (!offerData.taxDeclared) return { net: entered, vat: 0, patientTotal: entered, show: false };
    const exempt = offerData.taxTreatment === TAX_TREATMENT_EXEMPT;
    const rate = exempt ? 0 : (parseFloat(offerData.taxRatePercent) || 0) / 100;
    if (offerData.taxBasis === TAX_BASIS_EXCLUSIVE) {
      const vat = Math.round(entered * rate * 100) / 100;
      return { net: entered, vat, patientTotal: Math.round((entered + vat) * 100) / 100, show: true };
    }
    const net = Math.round((entered / (1 + rate)) * 100) / 100;
    return { net, vat: Math.round((entered - net) * 100) / 100, patientTotal: entered, show: true };
  };

  // TREATX-PLAN-1 — the provider preview is SERVER-DERIVED (see `planPreview`
  // below). The old client-side `calculatePaymentBreakdown` has been DELETED
  // rather than left dormant: it rounded a percentage deposit to whole currency
  // units, took it from the entered NET price and split instalments evenly, so
  // the composer displayed amounts the patient would never be charged. Nothing
  // may re-introduce contractual money arithmetic in JavaScript.
  const [planPreview, setPlanPreview] = useState({ loading: false, plan: null, error: null });

  const fmtMoney = (v) => Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  useEffect(() => {
    const base = parseFloat(offerData.basePrice);
    if (!Number.isFinite(base) || base <= 0 || !offerData.currency) {
      setPlanPreview({ loading: false, plan: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setPlanPreview((p) => ({ ...p, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/clinic/offers/payment-plan-preview', {
          base_price: base,
          currency: offerData.currency,
          deposit_type: offerData.depositType,
          deposit_value: offerData.depositValue === '' ? null : Number(offerData.depositValue),
          payment_structure: offerData.paymentStructure,
          installment_count: offerData.paymentStructure === 'installments'
            ? Number(offerData.installmentCount) : null,
          additional_services: (offerData.additionalServices || [])
            .filter((s) => Number(s.price) > 0)
            .map((s) => ({ name: s.name, price: Number(s.price), quantity: Number(s.quantity) || 1 })),
          provider_tax: buildProviderTaxPayload(offerData),
        });
        if (cancelled) return;
        const d = res.data || {};
        setPlanPreview(d.valid
          ? { loading: false, plan: d, error: null }
          : { loading: false, plan: null, error: d.error?.message || t('offer.plan.unavailable') });
      } catch {
        if (!cancelled) {
          setPlanPreview({ loading: false, plan: null, error: t('offer.plan.unavailable') });
        }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData.basePrice, offerData.currency, offerData.depositType, offerData.depositValue,
      offerData.paymentStructure, offerData.installmentCount, offerData.additionalServices,
      offerData.taxDeclared, offerData.taxBasis, offerData.taxTreatment,
      offerData.taxRatePercent, offerData.taxExemptionReason]);


  // TREATX-BATCH-10B (2026-09): the `terms` field is now STRICTLY
  // provider-authored, offer-specific Terms & Conditions. It no
  // longer falls back to the CMS `offer_default_payment_terms` /
  // `cancellation_policy_text` body — that was a semantic collision
  // that made the cancellation policy appear inside the T&C section.
  //
  // If the provider leaves this field empty, the patient web view and
  // PDF now OMIT the Terms & Conditions section entirely (rather than
  // silently substituting the cancellation policy body).
  //
  // The canonical Cancellation & Refund Policy is a SEPARATE section,
  // snapshotted immutably into the quote by the backend at issuance
  // time. It is never fabricated at read time.
  //
  // Prior behaviour deprecated 2026-09; see
  // /app/backend/services/offer_service.py::_freeze_cancellation_policy_snapshot.

  const handleSubmit = async (e, { draft = false } = {}) => {
    if (e) e.preventDefault();
    
    if (!offerData.basePrice) {
      toast({
        title: 'Missing information',
        description: 'Please enter the base price',
        variant: 'destructive'
      });
      return;
    }

    // PHASE 2B/B1 — issuance is blocked until the backend policy resolves,
    // and the chosen currency must belong to the backend-allowed set.
    // PROVIDER-OWNED-DRAFT-01 — a DRAFT is never issued, so it may carry a
    // PROPOSED currency while the policy is unresolved. The backend marks such
    // a draft `currency_verified: false` and refuses to issue it until the
    // patient's real residence makes the currency legal.
    if (!draft && !currencyPolicy?.resolved) {
      toast({ title: t('offer.currencyPolicy.unavailableTitle'), description: t(currencyPolicyReasonKey(currencyPolicy?.reason)), variant: 'destructive' });
      return;
    }
    if (!draft && !allowedCurrencies.includes(offerData.currency)) {
      toast({ title: t('offer.currencyPolicy.unavailableTitle'), description: t('offer.currencyPolicy.currencyNotAllowed', { allowed: allowedCurrencies.join(', ') }), variant: 'destructive' });
      return;
    }
    if (draft && !offerData.currency) {
      toast({ title: t('offer.currencyPolicy.unavailableTitle'), description: t('offer.currencyPolicy.currencyRequired'), variant: 'destructive' });
      return;
    }

    // FASTFIX-OFFER-COMPOSER-VALIDITY-AND-DISCOVERABILITY-01:
    // The backend contract is `valid_days` (integer 1-365).  Block the
    // submit at the UI boundary instead of letting the backend 422 it.
    // UAT-FIN-OFFER-SEND-COERCION — missing ≠ zero. Explicit deposit term required; backend is authority.
    const depositNum = offerData.depositValue === '' || offerData.depositValue === null || offerData.depositValue === undefined
      ? NaN : Number(offerData.depositValue);
    if (!Number.isFinite(depositNum) || depositNum < 0 || (offerData.depositType === 'percentage' && depositNum > 100)) {
      toast({ title: t('offer.payment.invalidDepositTitle'), description: t('offer.payment.invalidDepositDesc'), variant: 'destructive' });
      return;
    }
    if (offerData.paymentStructure === 'installments' && ![2, 3, 4].includes(Number(offerData.installmentCount))) {
      toast({ title: t('offer.payment.invalidInstallmentsTitle'), description: t('offer.payment.invalidInstallmentsDesc'), variant: 'destructive' });
      return;
    }
    // TREATX-TAX-5 — block an incomplete VAT declaration at the UI boundary;
    // the backend write-door fails closed on the same rule.
    const taxErrKey = validateProviderTax(offerData);
    if (taxErrKey) {
      toast({ title: t('offer.tax.errors.title'), description: t(taxErrKey), variant: 'destructive' });
      return;
    }

    const validDaysInt = parseInt(offerData.validUntil, 10);    if (!Number.isFinite(validDaysInt) || validDaysInt < 1 || validDaysInt > 365) {
      toast({
        title: 'Invalid validity period',
        description: 'Offer validity must be a whole number between 1 and 365 days.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    
    try {
      // TREATX-BATCH-10D — submit ONLY raw commercial inputs. The
      // client-derived `totalPrice` / `deposit` / `paymentBreakdown`
      // that this composer previously appended are removed: the backend
      // is the sole financial authority and already ignored them (grep
      // confirmed zero backend references). Removing them from the
      // payload closes the theoretical concern that a tampered client
      // could ever influence canonical financial truth by resending
      // stale/false derived numbers.
      const offer = {
        ...offerData,
        terms: offerData.terms || '',
        leadId: lead.id,
        patientId: lead.patient_id || lead.patientId,
        createdAt: new Date().toISOString(),
        status: 'SENT',
        // TREATX-BATCH-1.1 — Revision lineage. When the composer is
        // rendered against an `existingOffer` (revision mode), stamp
        // the immediate predecessor's id so the backend can persist
        // the canonical parent_offer_id + source_offer_id chain.
        // Previously the composer silently dropped this reference,
        // leaving composer-created v2/v3 offers unlinked.
        parentOfferId: isRevision ? (existingOffer?.id || null) : null,
        // MULTIPLE-ACTIONABLE-ALTERNATIVES — intent when starting from an existing offer.
        // revise = replaces the selected offer; alternative = additional co-valid option.
        // A fresh compose (not opened against a specific existing offer) is an ADDITIONAL
        // co-valid option — never an implicit revision of prior offers, so it never
        // supersedes siblings and needs no target.
        offer_mode: isRevision ? composerMode : 'alternative',
        supersedes_quote_id: (isRevision && composerMode === 'revise') ? (existingOffer?.id || null) : null,
        // PROVIDER-OWNED-DRAFT-01 — the backend decides the lifecycle state;
        // the composer only declares the intent.
        draft,
      };
      if (draft) offer.status = 'DRAFT';

      // P0-A FIX: Await the parent callback so errors propagate properly
      await onOfferSent(offer);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to send offer',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // P3-FIX-2: Section IDs for navigation
  const sections = [
    { id: 'pricing', label: t('offer.stepper.pricing'), icon: DollarSign },
    { id: 'included', label: t('offer.stepper.included'), icon: FileText },
    { id: 'addons', label: t('offer.stepper.addons'), icon: Plus },
    { id: 'travel', label: t('offer.stepper.travel'), icon: Plane },
    { id: 'payment', label: t('offer.stepper.payment'), icon: CreditCard },
    { id: 'terms', label: t('offer.stepper.terms'), icon: FileText },
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(`offer-section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {isRevision && (
        <div className="rounded-lg border border-slate-200 p-3" data-testid="offer-mode-toggle">
          <p className="text-xs font-medium text-slate-600 mb-2">{t('offer.mode.title')}</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setComposerMode('revise')}
              className={`flex-1 text-left rounded-md border px-3 py-2 text-xs ${composerMode === 'revise' ? 'border-[#3BA4C5] bg-[#3BA4C5]/5 ring-1 ring-[#3BA4C5]' : 'border-slate-200'}`}
              data-testid="offer-mode-revise">
              <span className="font-semibold block">{t('offer.mode.reviseTitle')}</span>
              <span className="text-slate-500">{t('offer.mode.reviseDesc')}</span>
            </button>
            <button type="button" onClick={() => setComposerMode('alternative')}
              className={`flex-1 text-left rounded-md border px-3 py-2 text-xs ${composerMode === 'alternative' ? 'border-[#3BA4C5] bg-[#3BA4C5]/5 ring-1 ring-[#3BA4C5]' : 'border-slate-200'}`}
              data-testid="offer-mode-alternative">
              <span className="font-semibold block">{t('offer.mode.altTitle')}</span>
              <span className="text-slate-500">{t('offer.mode.altDesc')}</span>
            </button>
          </div>
        </div>
      )}
      {/* FASTFIX-OFFER-COMPOSER-FEATURE-PARITY-01 — additive panels */}
      {/* ── SSO-D (audit defect D7) — WHAT THE PATIENT ACTUALLY ASKED FOR ──
          The attribution, doctor preference and original wording already
          travelled with the lead but were rendered nowhere in the rep's
          workspace, so a quote could be built without ever seeing the
          patient's choice. Read-only by design: this panel states the
          ORIGINAL request; the form below is the provider's PROPOSAL. */}
      {(lead?.campaign_attribution || lead?.preferred_doctor || lead?.treatment_interest) && (
        <div
          className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-xs text-slate-800"
          data-testid="offer-composer-patient-selection"
        >
          <div className="font-semibold mb-1">The patient's original request</div>
          <div data-testid="offer-composer-patient-treatment">
            Treatment: <span className="font-medium">{lead.treatment_interest || '—'}</span>
          </div>
          {lead.campaign_attribution ? (
            <div className="mt-0.5" data-testid="offer-composer-patient-offer">
              International Offer:{' '}
              <span className="font-medium">
                {lead.campaign_attribution.campaign_title || lead.campaign_attribution.campaign_id}
              </span>
              {lead.campaign_attribution.discount_value != null && (
                <> · {lead.campaign_attribution.discount_value}
                  {lead.campaign_attribution.discount_type === 'percentage' ? '%' : ''}</>
              )}
              {lead.campaign_attribution.valid_until && (
                <> · valid until {String(lead.campaign_attribution.valid_until).slice(0, 10)}</>
              )}
              <span className="ml-1 text-slate-500" data-testid="offer-composer-patient-provenance">
                ({lead.campaign_attribution.patient_selected === true
                  ? 'chosen by the patient'
                  : lead.campaign_attribution.patient_selected === false
                    ? 'attached automatically — not an explicit patient choice'
                    : 'provenance unknown (legacy record)'})
              </span>
            </div>
          ) : (
            <div className="mt-0.5" data-testid="offer-composer-patient-standard">
              International Offer: <span className="font-medium">none — standard treatment requested</span>
            </div>
          )}
          {lead.preferred_doctor?.doctor_id && (
            <div className="mt-0.5" data-testid="offer-composer-patient-doctor">
              Preferred doctor:{' '}
              <span className="font-medium">
                {lead.preferred_doctor.full_name || lead.preferred_doctor.doctor_id}
              </span>
              <span className="ml-1 text-slate-500">(a preference, not an assignment)</span>
            </div>
          )}
          {Array.isArray(lead.selection_events) && lead.selection_events.length > 1 && (
            <div className="mt-1 pt-1 border-t border-slate-200" data-testid="offer-composer-latest-selection">
              <span className="text-slate-500">Latest patient request: </span>
              <span className="font-medium">
                {latestPatientCampaignId(lead) || 'standard treatment'}
              </span>
              <span className="text-slate-500">
                {' '}· updated {lead.selection_events.length - 1} time(s) — the form below starts from this
              </span>
            </div>
          )}
        </div>
      )}

      {showPositioningBlock && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900"
          data-testid="offer-composer-positioning-block"
        >
          <div className="font-semibold mb-1">Your positioning for this patient</div>
          {positioning.tags.length > 0 && (
            <ul className="space-y-0.5" data-testid="offer-composer-positioning-tags">
              {positioning.tags.map((t, i) => <li key={i}>• {t.label || t.tag || t}</li>)}
            </ul>
          )}
          {positioning.vips.length > 0 && (
            <ul className="space-y-0.5 mt-1" data-testid="offer-composer-positioning-vips">
              {positioning.vips.map((v, i) => <li key={i}>★ {v.label || v.vip || v}</li>)}
            </ul>
          )}
          {positioning.campaign?.has_active_campaign && (
            <div
              className="mt-1 font-medium text-rose-700"
              data-testid="offer-composer-positioning-campaign"
            >
              🔥 {positioning.campaign.campaign_description || 'Active campaign'}
            </div>
          )}
        </div>
      )}

      {(enableTemplates || enableCampaigns) && (
        <div className="space-y-2" data-testid="offer-composer-quick-pickers">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {enableTemplates && (
            <div>
              <Label className="text-xs">Apply template</Label>
              {templateList.length === 0 ? (
                <div
                  className="mt-1 w-full text-xs text-slate-500 border border-dashed border-slate-200 bg-slate-50 rounded-md px-2 py-2"
                  data-testid="offer-composer-template-empty"
                >
                  No offer templates available for this clinic.
                </div>
              ) : (
                <select
                  className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
                  value={offerData.templateId || ''}
                  onChange={(e) => {
                    const tpl = templateList.find(t => (t.id || t.template_id) === e.target.value);
                    applyTemplate(tpl);
                  }}
                  data-testid="offer-composer-template-picker"
                >
                  <option value="">— No template —</option>
                  {templateList.map(t => (
                    <option key={t.id || t.template_id} value={t.id || t.template_id}>
                      {t.template_name || t.name || t.treatment_slug || t.id}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {enableCampaigns && (
            <div>
              <Label className="text-xs">Apply campaign</Label>
              {campaignList.length === 0 ? (
                <div
                  className="mt-1 w-full text-xs text-slate-500 border border-dashed border-slate-200 bg-slate-50 rounded-md px-2 py-2"
                  data-testid="offer-composer-campaign-empty"
                >
                  No active campaigns available.
                </div>
              ) : (
                <select
                  className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
                  value={offerData.campaignId || ''}
                  onChange={(e) => {
                    const camp = campaignList.find(c => (c.campaign_id || c.id) === e.target.value);
                    applyCampaign(camp);
                  }}
                  data-testid="offer-composer-campaign-picker"
                >
                  <option value="">— No campaign —</option>
                  {campaignList.map(c => (
                    <option key={c.campaign_id || c.id} value={c.campaign_id || c.id}>
                      {c.title || c.campaign_title || c.name || c.campaign_id || c.id}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Discount %</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={offerData.discountPercentage}
              onChange={(e) => setOfferData(d => ({ ...d, discountPercentage: e.target.value }))}
              placeholder="0"
              data-testid="offer-composer-discount-input"
              className="mt-1"
            />
          </div>
          </div>
          {((enableTemplates && templateList.length === 0) || (enableCampaigns && campaignList.length === 0)) && (
            <p
              className="text-[11px] text-slate-400"
              data-testid="offer-composer-pickers-hint"
            >
              Templates and campaigns can be managed from the clinic administration area.
            </p>
          )}
        </div>
      )}

      {/* P3-REVISION: Revision banner */}
      {isRevision && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 -mt-2 mb-4">
          <div className="flex items-center gap-2 text-amber-800">
            <FileText className="h-4 w-4" />
            <span className="font-medium">{t('offer.revisingBanner', { from: (existingOffer.version_number ?? existingOffer.version ?? 1), to: nextVersion })}</span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            {t('offer.revisingNote')}
          </p>
        </div>
      )}
      
      {/* P3-FIX-2: Sticky Section Navigation */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm -mx-6 px-6 py-2 -mt-6 mb-4">
        <div className="flex items-center gap-1 overflow-x-auto">
          {sections.map((section, idx) => (
            <button
              key={section.id}
              type="button"
              onClick={() => scrollToSection(section.id)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <section.icon className="h-3 w-3" />
              {section.label}
              {idx < sections.length - 1 && <ChevronRight className="h-3 w-3 text-gray-300 ml-1" />}
            </button>
          ))}
        </div>
      </div>

      {/* Treatment & Pricing */}
      <Card id="offer-section-pricing">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {t('offer.pricing.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('offer.pricing.treatment')}</Label>
            <Input
              value={offerData.treatment}
              onChange={(e) => setOfferData({ ...offerData, treatment: e.target.value })}
              placeholder="e.g., Hair Transplant FUE (3500 grafts)"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('offer.pricing.basePrice')} *</Label>
              <Input
                type="number"
                step="0.01"
                value={offerData.basePrice}
                onChange={(e) => setOfferData({ ...offerData, basePrice: e.target.value })}
                placeholder="2500"
                required
              />
            </div>
            <div>
              <Label>{t('offer.pricing.currency')}</Label>
              <select
                value={offerData.currency}
                onChange={(e) => setOfferData({ ...offerData, currency: e.target.value })}
                className="w-full p-2 border rounded-lg"
                disabled={!currencyPolicy}
                data-testid="offer-currency-select"
              >
                {!currencyPolicy && <option value="">{t('offer.currencyPolicy.loading')}</option>}
                {/* The visible option must match state: without a placeholder
                    the browser shows the first currency while `currency` is
                    still empty, and the save is then refused. */}
                {currencyPolicy && !offerData.currency && (
                  <option value="">{t('offer.currencyPolicy.choose', 'Select currency')}</option>
                )}
                {(currencyPolicy?.resolved ? allowedCurrencies : (currencyPolicy ? DRAFT_PROPOSAL_CURRENCIES : [])).map(c => (
                  <option key={c} value={c}>{c} ({currencySymbol(c).trim()})</option>
                ))}
              </select>
              {currencyPolicy?.resolved && allowedCurrencies.length === 1 && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="offer-currency-only-hint">
                  {t('offer.currencyPolicy.onlyCurrency', { currency: allowedCurrencies[0] })}
                </p>
              )}
            </div>
          </div>
          {/* TREATX-TAX-5 — provider treatment VAT, in the price step. */}
          <div className="mt-4">
            <ProviderTaxSection
              data={offerData}
              onChange={(next) => setOfferData({ ...offerData, ...next })}
              currencySym={sym}
            />
          </div>
          {policyBlocked && (            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="offer-currency-policy-blocked">
              <p className="font-semibold">{t('offer.currencyPolicy.unavailableTitle')}</p>
              <p>{t(currencyPolicyReasonKey(currencyPolicy.reason))}</p>
              {/* PROVIDER-OWNED-RESIDENCE-01 — the representative records the
                  patient's ACTUAL residence here instead of abandoning the task.
                  Reuses the canonical country catalogue + form; the writer is the
                  lead-scoped endpoint, which persists through the SAME fill-once
                  `persons.residence_country` authority. */}
              {currencyPolicy.reason === 'patient_residence_unresolved' && policyLeadId && (
                <div className="mt-3 rounded-md border border-amber-200 bg-white p-3" data-testid="offer-residence-capture">
                  <p className="text-xs font-semibold text-slate-800">{t('offer.residence.title')}</p>
                  <p className="text-xs text-slate-600 mb-2">{t('offer.residence.description')}</p>
                  <ResidenceForm
                    compact
                    copy={{ label: t('offer.residence.label'),
                            placeholder: t('offer.residence.placeholder'),
                            hint: '',
                            save: t('offer.residence.save') }}
                    writer={async (value) => {
                      const { data } = await api.post(
                        `/clinic-leads/${policyLeadId}/patient-residence`,
                        { residence_country: value });
                      return data;
                    }}
                    onDeclared={(value, data) => {
                      const pol = data?.currency_policy;
                      if (pol) {
                        setCurrencyPolicy(pol);
                        const allowed = pol.allowed_currencies || [];
                        setOfferData(d => (pol.resolved && !allowed.includes(d.currency))
                          ? { ...d, currency: allowed[0] || '' } : d);
                      }
                      toast({ title: t('offer.residence.savedTitle'),
                              description: t('offer.residence.savedDesc') });
                    }}
                  />
                </div>
              )}
              <p className="mt-2 text-xs" data-testid="offer-draft-still-possible">
                {t('offer.draft.currencyUnverified')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* What's Included */}
      <Card id="offer-section-included">
        <CardHeader>
          <CardTitle>{t('offer.included.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {offerData.includedItems.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm">{item.name}</span>
              <Badge variant="outline" className="bg-green-50 text-green-700">{t('offer.travel.included')}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Additional Services */}
      <Card id="offer-section-addons">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            {t('offer.addons.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Available Services */}
          <div className="grid grid-cols-2 gap-2">
            {packageOptions.map((service, idx) => (
              <Button
                key={idx}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addAdditionalService(service)}
                disabled={offerData.additionalServices.find(s => s.name === service.name)}
              >
                <Plus className="h-3 w-3 mr-2" />
                {service.name}
              </Button>
            ))}
          </div>

          {/* Selected Services */}
          {offerData.additionalServices.length > 0 && (
            <div className="space-y-2 pt-4 border-t">
              <p className="text-sm font-medium">{t('offer.addons.selectedServices')}:</p>
              {offerData.additionalServices.map((service, idx) => {
                const lineTotal = service.price * service.quantity;
                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium block truncate">{service.name}</span>
                      {/* SPRINT-2: Live line total display */}
                      <span className="text-xs text-blue-600 font-medium">
                        {t('offer.addons.lineTotal')}: {sym}{lineTotal.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {/* SPRINT-2: Editable unit price */}
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-gray-500 mb-0.5">{t('offer.addons.unitPrice')}</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={service.price}
                          onChange={(e) => updateServicePrice(service.name, e.target.value)}
                          className="w-20 text-center text-sm"
                        />
                      </div>
                      {/* Quantity input */}
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-gray-500 mb-0.5">{t('offer.addons.qty')}</span>
                        <Input
                          type="number"
                          min="1"
                          value={service.quantity}
                          onChange={(e) => updateServiceQuantity(service.name, parseInt(e.target.value))}
                          className="w-16 text-center text-sm"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAdditionalService(service.name)}
                        className="mt-4"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Accommodation & Travel */}
      <Card id="offer-section-travel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hotel className="h-5 w-5" />
            {t('offer.travel.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('offer.travel.hotelNights')}</Label>
              <Input
                type="number"
                value={offerData.hotelNights}
                onChange={(e) => setOfferData({ ...offerData, hotelNights: e.target.value })}
                placeholder="7"
              />
            </div>
            <div>
              <Label>{t('offer.travel.hotelType')}</Label>
              <select
                value={offerData.hotelType}
                onChange={(e) => setOfferData({ ...offerData, hotelType: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">Select type</option>
                <option value="3-star">3-Star</option>
                <option value="4-star">4-Star</option>
                <option value="5-star">5-Star</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposed Dates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('offer.travel.proposedDates')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('offer.travel.proposedDates')}</Label>
            <Textarea
              value={offerData.proposedDates}
              onChange={(e) => setOfferData({ ...offerData, proposedDates: e.target.value })}
              placeholder="e.g., Week of March 15-22, 2024&#10;Day 1: Arrival & Consultation&#10;Day 2: Procedure&#10;Days 3-6: Recovery&#10;Day 7: Final check & Departure"
              rows={5}
            />
          </div>

          <div data-testid="offer-composer-validity-block">
            {/* FASTFIX-OFFER-COMPOSER-VALIDITY-AND-DISCOVERABILITY-01:
                Backend contract is `valid_days` (integer 1-365). The field
                used to render as <input type="date">, which led owners to
                pick a calendar date and submit a value the backend rejected
                with "valid days must be 1-365". We now render it as an
                unambiguous numeric duration. No backend / API change. */}
            <Label htmlFor="offer-valid-days">Valid for (days)</Label>
            <Input
              id="offer-valid-days"
              type="number"
              min="1"
              max="365"
              step="1"
              inputMode="numeric"
              value={offerData.validUntil}
              onChange={(e) => setOfferData({ ...offerData, validUntil: e.target.value.replace(/[^0-9]/g, '') })}
              placeholder="14"
              data-testid="offer-composer-valid-days-input"
            />
            <p className="text-xs text-slate-500 mt-1" data-testid="offer-composer-valid-days-helper">
              Offer validity period (1–365 days).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* P3-FIX-2: Payment Structure Customization */}
      <Card id="offer-section-payment" className="border-2 border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <CreditCard className="h-5 w-5" />
            {t('offer.payment.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Deposit Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('offer.payment.depositType')}</Label>
              <select
                value={offerData.depositType}
                onChange={(e) => setOfferData({ ...offerData, depositType: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="percentage">{t('offer.payment.percentage')}</option>
                <option value="fixed">{t('offer.payment.fixedAmount')}</option>
              </select>
            </div>
            <div>
              <Label>{t('offer.payment.depositValue')}</Label>
              <Input
                type="number"
                min="0"
                max={offerData.depositType === 'percentage' ? 100 : undefined}
                value={offerData.depositValue}
                onChange={(e) => setOfferData({ ...offerData, depositValue: e.target.value })}
                data-testid="offer-deposit-value-input"
                placeholder={offerData.depositType === 'percentage' ? '30' : '1000'}
              />
            </div>
          </div>

          {/* Balance Payment Structure */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('offer.payment.title')}</Label>
              <select
                value={offerData.paymentStructure}
                onChange={(e) => setOfferData({ ...offerData, paymentStructure: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="single">{t('offer.payment.singlePayment')}</option>
                <option value="installments">{t('offer.payment.installments')}</option>
              </select>
            </div>
            {offerData.paymentStructure === 'installments' && (
              <div>
                <Label>{t('offer.payment.numberOfInstallments')}</Label>
                <select
                  value={offerData.installmentCount}
                  onChange={(e) => setOfferData({ ...offerData, installmentCount: parseInt(e.target.value) })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="2">2 Installments</option>
                  <option value="3">3 Installments</option>
                  <option value="4">4 Installments</option>
                </select>
              </div>
            )}
          </div>

          {/* TREATX-PLAN-1 — canonical payment-plan preview.
              Rendered from the SERVER derivation (/clinic/offers/payment-plan-preview),
              which runs the same gross-up, deposit and split authorities as the
              write door — so what the provider sees here is exactly what the
              patient will be asked to pay. No client-side money maths. */}
          {offerData.basePrice && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm" data-testid="offer-payment-plan-preview">
              <p className="font-medium mb-2">{t('offer.payment.title')}:</p>
              {planPreview.loading && (
                <p className="text-gray-500" data-testid="offer-plan-loading">{t('offer.plan.calculating')}</p>
              )}
              {!planPreview.loading && planPreview.error && (
                <p className="text-red-600" data-testid="offer-plan-error">{planPreview.error}</p>
              )}
              {!planPreview.loading && planPreview.plan && (
                <div className="space-y-1 text-gray-600">
                  <p data-testid="offer-plan-total">
                    • {t('offer.plan.payableTotal')}: {sym}{fmtMoney(planPreview.plan.total)}
                  </p>
                  {planPreview.plan.collections.map((c) => (
                    <p key={c.sequence} data-testid={`offer-plan-collection-${c.sequence}`}>
                      • {c.collection_type === 'deposit'
                          ? t('offer.pricing.deposit')
                          : c.collection_type === 'installment'
                            ? `${t('offer.plan.installmentLabel')} ${c.installment_number}/${c.installment_count}`
                            : c.collection_type === 'balance'
                              ? t('offer.pricing.remaining')
                              : t('offer.plan.fullPayment')}
                      : {sym}{fmtMoney(c.amount)}
                      {c.collection_type === 'deposit' && offerData.depositType === 'percentage'
                        ? ` (${offerData.depositValue}%)` : ''}
                    </p>
                  ))}
                  <p className="text-xs text-gray-500 pt-1" data-testid="offer-plan-collection-count">
                    {t('offer.plan.collectionCount', { count: planPreview.plan.collection_count })}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes & Terms */}
      <Card id="offer-section-terms">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('offer.terms.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('offer.terms.notesToPatient')}</Label>
            <Textarea
              value={offerData.notes}
              onChange={(e) => setOfferData({ ...offerData, notes: e.target.value })}
              placeholder={t('offer.terms.termsPlaceholder')}
              rows={3}
            />
          </div>

          <div>
            <Label>{t('offer.terms.termsAndConditions', 'Terms & Conditions (optional)')}</Label>
            <Textarea
              value={offerData.terms}
              onChange={(e) => setOfferData({ ...offerData, terms: e.target.value })}
              rows={4}
              placeholder={t('offer.terms.termsPlaceholder')}
              data-testid="offer-terms-and-conditions-input"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              {t('offer.terms.termsHelper',
                'Provider-authored offer-specific conditions only. If left empty, this section will be omitted from the offer.')}
            </p>
          </div>

          {/* TREATX-BATCH-10B — Cancellation & Refund Policy preview.
              Read-only: the canonical policy is snapshotted immutably
              by the backend when the offer is sent. Editing the policy
              body must be done via the CMS admin (platform_content). */}
          <div className="rounded-md border border-blue-100 bg-blue-50/40 p-3" data-testid="offer-cancellation-policy-preview">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-semibold text-blue-900">
                {t('offer.terms.cancellationPolicy', 'Cancellation & Refund Policy')}
              </Label>
              <span className="text-[10px] uppercase tracking-wider text-blue-700">
                {t('offer.terms.snapshotBadge', 'Snapshotted at send')}
              </span>
            </div>
            {cancellationPolicyContent?.title || cancellationPolicyContent?.summary ? (
              <div className="space-y-1">
                {cancellationPolicyContent?.summary ? (
                  <p className="text-xs text-blue-900/80">{cancellationPolicyContent.summary}</p>
                ) : null}
                <a
                  href={`/legal/cancellation_and_refund_conditions`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-blue-800 underline"
                  data-testid="composer-cancellation-view-full"
                >
                  {t('legal.cancellationConditions.viewFull', 'View Cancellation & Refund Conditions')}
                </a>
                <p className="text-[10px] text-blue-700/70">
                  {cancellationPolicyContent?.title || 'Cancellation & Refund Conditions'}
                  {cancellationPolicyContent?.version != null ? ` · v${cancellationPolicyContent.version}` : ''}
                </p>
              </div>
            ) : (
              <p className="text-xs text-blue-900/70 italic">
                {t('offer.terms.cancellationPolicyUnavailable',
                  'No canonical cancellation policy is currently published; the offer will still be sent without a policy snapshot.')}
              </p>
            )}
            <p className="text-[10px] text-blue-700/70 mt-2">
              {t('offer.terms.snapshotHelper',
                'This is the current provider policy. When you send this offer, the exact wording above will be captured as an immutable snapshot so the patient always sees the policy that was in effect at issuance.')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Total & Submit */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-gray-600">{t('offer.pricing.totalPackagePrice')}</p>
              <p className="text-3xl font-bold text-blue-600">
                {sym}{calculateTotalPrice().toLocaleString()}
              </p>
            </div>
            <div className="text-right text-sm text-gray-600">
              <div>{t('offer.pricing.base')}: {sym}{parseFloat(offerData.basePrice || 0).toLocaleString()}</div>
              {offerData.additionalServices.length > 0 && (
                <div>{t('offer.pricing.extras')}: {sym}{offerData.additionalServices.reduce((sum, s) => sum + (s.price * s.quantity), 0).toLocaleString()}</div>
              )}
            </div>
          </div>

          {/* TREATX-TAX-5 — net / VAT / patient total preview */}
          {(() => {
            const tp = taxPreview();
            if (!tp.show) return null;
            const money = (v) => `${sym}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
            return (
              <div className="mb-6 rounded-lg bg-white/70 p-3 text-sm text-gray-700 space-y-1" data-testid="offer-tax-preview">
                <div className="flex justify-between"><span>{t('offer.tax.netAmount')}</span><span className="font-medium">{money(tp.net)}</span></div>
                <div className="flex justify-between">
                  <span>{offerData.taxTreatment === TAX_TREATMENT_EXEMPT
                    ? t('offer.tax.treatmentExempt')
                    : t('offer.tax.vatAt', { rate: parseFloat(offerData.taxRatePercent) || 0 })}</span>
                  <span className="font-medium">{money(tp.vat)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-gray-200">
                  <span className="font-semibold">{t('offer.tax.patientTotal')}</span>
                  <span className="font-bold" data-testid="offer-tax-preview-patient-total">{money(tp.patientTotal)}</span>
                </div>
              </div>
            );
          })()}

          {/* P3-REVISION: Version info */}
          {isRevision && (
            <div className="mb-4 p-2 bg-white/50 rounded text-center text-sm text-gray-600">
              {t('offer.version', { version: nextVersion })}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            {/* PROVIDER-OWNED-DRAFT-01 — a provider-owned patient often has no
                TreatX login, so "send to patient" is not the only practical
                action. A DRAFT is prepared, reviewed and printed without
                sending anything; the backend marks it DRAFT and never notifies. */}
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={() => handleSubmit(null, { draft: true })}
              data-testid="offer-create-draft-btn"
              className="flex-1"
            >
              {loading ? t('common.loading') : t('offer.draft.createButton')}
            </Button>
            <Button
              type="submit"
              disabled={loading || !currencyPolicy?.resolved}
              data-testid="offer-submit-btn"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {loading ? t('common.loading') : isRevision ? t('offer.sendRevisedOffer') : t('offer.sendOffer')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default OfferComposer;
